import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Card, Col, Row, Form, Table, Dropdown, Modal } from 'react-bootstrap';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import axiosInstance from '../services/axiosInterceptor';
import { getToken } from '../services/authorize';
import { useLoading } from '../contexts/LoadingContext';
import { useAuth } from '../services/authProvider';
import { getAcademicReportRoleLabel, getLevelSuffix } from '../utils/roleLabels';

const API = process.env.REACT_APP_API;

const UPLOAD_BASE = (process.env.REACT_APP_API || 'http://localhost:3002/api').replace(/\/api\/?$/, '').replace(/\/$/, '');
const StudentAvatar = ({ photo, name }) => {
  const [hovered, setHovered] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const initial = name ? name.trim().charAt(0) : '?';
  const circleStyle = { width: 28, height: 28, borderRadius: '50%', flexShrink: 0 };
  const src = photo ? `${UPLOAD_BASE}${photo.startsWith('/') ? photo : `/${photo}`}` : null;
  return (
    <>
      <span
        style={{ position: 'relative', display: 'inline-block', lineHeight: 0 }}
        onMouseEnter={() => src && setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => src && setShowModal(true)}
      >
        {src
          ? <img src={src} alt="" style={{ ...circleStyle, objectFit: 'cover', cursor: 'pointer' }} />
          : <span style={{ ...circleStyle, backgroundColor: '#adb5bd', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600 }}>{initial}</span>
        }
        {src && hovered && (
          <div style={{ position: 'absolute', left: 34, top: '50%', transform: 'translateY(-50%)', zIndex: 1050, background: '#fff', borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.25)', padding: 4 }}>
            <img src={src} alt={name} style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 6, display: 'block' }} />
          </div>
        )}
      </span>
      {src && (
        <Modal show={showModal} onHide={() => setShowModal(false)} centered size="sm">
          <Modal.Body className="p-3 text-center">
            <img src={src} alt={name} style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 8 }} />
            {name && <div className="mt-2 fw-semibold small">{name}</div>}
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};

// การ์ดนักเรียน 1 คน สำหรับมุมมองมือถือ
const StudentRoomCard = ({ s, index, onDormClick }) => (
  <div className={`border rounded p-3 ${s.st_status === '0' ? 'bg-danger-subtle' : s.transfer_room_id ? 'bg-warning-subtle' : ''}`}>
    <div className="d-flex align-items-center gap-2">
      <StudentAvatar photo={s.st_photo} name={s.st_name} />
      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        <div className="fw-semibold text-truncate">{s.st_name}</div>
        <div className="fs-xs text-muted">#{index + 1} · {s.st_idnumber}</div>
        {s.transfer_room_id && (
          <div className="fs-xs text-muted">(ย้ายไป ม.{s.transfer_class_id}/{s.transfer_room_no})</div>
        )}
      </div>
    </div>
    <div className="mt-2 pt-2 border-top fs-sm">
      <div>
        <span className="text-muted">ชั้นศาสนา:</span>{' '}
        {s.ic_class ? `${s.ic_class}/${s.isl_room_no}` : <span className="text-muted">-</span>}
      </div>
      <div>
        <span className="text-muted">หอพัก:</span>{' '}
        {s.dorm_name ? (
          <span className="text-primary" style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => onDormClick(s)}>
            {s.dorm_name}
          </span>
        ) : <span className="text-muted">-</span>}
      </div>
      <div className={s.st_status === '0' ? 'text-danger' : s.st_status === '1' ? 'text-primary' : 'text-success'}>
        {s.status_text}
      </div>
    </div>
    <div className="mt-2 pt-2 border-top">
      <Link to={`/academic/student-info/${s.st_id}`} className="btn btn-sm btn-outline-primary w-100">
        <i className="ri-eye-line me-1"></i>ดูข้อมูล
      </Link>
    </div>
  </div>
)

export default function StudentListByRoom() {
  const { setLoading } = useLoading();
  const { posIds } = useAuth();
  const roleLabel = getAcademicReportRoleLabel(posIds);
  const levelSuffix = getLevelSuffix(posIds);
  const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
  const [skin, setSkin] = useState(currentSkin);

  const switchSkin = (s) => {
    const tables = document.getElementsByTagName('table');
    for (const t of tables) {
      const theads = t.querySelectorAll('thead');
      if (s === 'dark') theads.forEach((th) => th.classList.add('table-dark'));
      else theads.forEach((th) => th.classList.remove('table-dark'));
    }
  };
  useEffect(() => { switchSkin(skin); });

  const [yearList, setYearList] = useState([]);
  const [yearId, setYearId] = useState(() => sessionStorage.getItem('slbr_yearId') || null);
  const [roomList, setRoomList] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(() => sessionStorage.getItem('slbr_room') || '');
  const [students, setStudents] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [dormModal, setDormModal] = useState(null); // { dorm_id, dorm_name, village_name, st_id }
  const [dormResidents, setDormResidents] = useState([]);
  const [dormLoaded, setDormLoaded] = useState(false);
  const [view, setView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table');

  useEffect(() => {
    axiosInstance
      .get(`${API}/getStudyYear`, { headers: { authorization: `Bearer ${getToken()}` } })
      .then((res) => {
        const list = res.data || [];
        setYearList(list);
        // ตั้งค่าปีเริ่มต้นเฉพาะเมื่อยังไม่มีค่าที่จำไว้จาก sessionStorage
        if (list.length > 0) setYearId((prev) => prev || list[0].year_id);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!yearId) return;
    axiosInstance
      .get(`${API}/getRoomListByYear/${yearId}`, { headers: { authorization: `Bearer ${getToken()}` } })
      .then((res) => setRoomList(res.data || []))
      .catch(() => setRoomList([]));
  }, [yearId]);

  // จำปี/ห้องที่เลือกไว้ เพื่อให้ตอนกดย้อนกลับจากหน้าข้อมูลนักเรียนยังอยู่ที่ห้องเดิม
  useEffect(() => {
    if (yearId) sessionStorage.setItem('slbr_yearId', String(yearId));
  }, [yearId]);
  useEffect(() => {
    sessionStorage.setItem('slbr_room', selectedRoom || '');
  }, [selectedRoom]);

  useEffect(() => {
    if (!selectedRoom) { setStudents([]); setLoaded(false); return; }
    setLoading(true);
    setLoaded(false);
    axiosInstance
      .get(`${API}/listStudentsInClass/${selectedRoom}`, { headers: { authorization: `Bearer ${getToken()}` } })
      .then((res) => setStudents(res.data || []))
      .catch(() => setStudents([]))
      .finally(() => {
        setLoaded(true);
        setLoading(false);
      });
  }, [setLoading, selectedRoom]);

  const openDormModal = (student) => {
    setDormModal({
      dorm_id: student.dorm_id,
      dorm_name: student.dorm_name,
      dorm_code: student.dorm_code,
      village_name: student.village_name,
      st_id: student.st_id,
    });
    setDormResidents([]);
    setDormLoaded(false);
    axiosInstance
      .get(`${API}/getDormResidents/${student.dorm_id}`, { headers: { authorization: `Bearer ${getToken()}` } })
      .then((res) => setDormResidents(res.data || []))
      .catch(() => setDormResidents([]))
      .finally(() => setDormLoaded(true));
  };

  const selectedYearLabel = useMemo(() => {
    const y = yearList.find((item) => String(item.year_id) === String(yearId));
    return y ? `ปีการศึกษา ${y.year_year}` : 'เลือกปีการศึกษา';
  }, [yearList, yearId]);

  const roomLabel = useMemo(() => {
    const room = roomList.find((r) => String(r.r_id) === String(selectedRoom));
    return room ? `ม.${room.c_class || room.c_id}/${room.r_no}${room.p_code ? ` (${room.p_code})` : ''}` : '';
  }, [roomList, selectedRoom]);

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">{roleLabel}</Link></li>
              <li className="breadcrumb-item active" aria-current="page">รายชื่อนักเรียนรายห้อง</li>
            </ol>
            <h4 className="main-title mb-0">รายชื่อนักเรียนรายห้อง{levelSuffix}</h4>
          </div>

          <div className="d-flex align-items-center gap-2 mt-3 mt-md-0">
            <Dropdown>
              <Dropdown.Toggle variant="primary" id="dropdown-year">
                {selectedYearLabel}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {yearList.map((item) => (
                  <Dropdown.Item
                    key={item.year_id}
                    onClick={() => {
                      if (String(item.year_id) !== String(yearId)) setSelectedRoom('');
                      setYearId(item.year_id);
                    }}
                  >
                    {`ปีการศึกษา ${item.year_year}`}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        <Card className="card-one mb-3">
          <Card.Body>
            <Row className="g-2 align-items-end">
              <Col md={5}>
                <Form.Label className="fs-sm mb-1">เลือกห้องเรียน</Form.Label>
                <Form.Select value={selectedRoom} onChange={(e) => setSelectedRoom(e.target.value)}>
                  <option value="">-- เลือกห้องเรียน --</option>
                  {roomList.map((r) => (
                    <option key={r.r_id} value={r.r_id}>
                      ม.{r.c_class || r.c_id}/{r.r_no}{r.p_code ? ` (${r.p_code})` : ''}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {selectedRoom && (
          <Card className="card-one mb-4">
            <Card.Header className="d-flex flex-wrap align-items-center gap-2">
              <Card.Title as="h6" className="mb-0">
                รายชื่อนักเรียนห้อง {roomLabel}
                <Badge bg="secondary" className="ms-2">{students.length} คน</Badge>
              </Card.Title>
              <div className="btn-group btn-group-sm ms-auto" role="group" aria-label="สลับมุมมอง">
                <button type="button" className={`btn btn${view === 'table' ? '' : '-outline'}-primary`}
                  onClick={() => setView('table')} title="มุมมองตาราง">
                  <i className="ri-table-line" />
                </button>
                <button type="button" className={`btn btn${view === 'card' ? '' : '-outline'}-primary`}
                  onClick={() => setView('card')} title="มุมมองการ์ด">
                  <i className="ri-layout-grid-line" />
                </button>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {loaded && students.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <i className="ri-team-line fs-3 d-block mb-2"></i>
                  ไม่พบนักเรียนในห้องนี้
                </div>
              ) : view === 'card' ? (
                <div className="p-3">
                  <Row className="g-3">
                    {students.map((s, index) => (
                      <Col key={s.st_id} xs={12} md={6} xl={4}>
                        <StudentRoomCard s={s} index={index} onDormClick={openDormModal} />
                      </Col>
                    ))}
                  </Row>
                </div>
              ) : (
                <Table hover responsive className="mb-0 align-middle">
                  <thead className="table-secondary">
                    <tr>
                      <th scope="col" className="text-center" style={{ width: 60 }}>เลขที่</th>
                      <th scope="col" className="text-center" style={{ width: 110 }}>เลขประจำตัว</th>
                      <th scope="col">ชื่อ-สกุล</th>
                      <th scope="col" className="text-center" style={{ width: 110 }}>ชั้นศาสนา</th>
                      <th scope="col" className="text-center" style={{ width: 160 }}>หอพัก</th>
                      <th scope="col" className="text-center" style={{ width: 150 }}>สถานะ</th>
                      <th scope="col" className="text-center" style={{ width: 110 }}>ข้อมูล</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((s, index) => (
                      <tr key={s.st_id} className={s.st_status === '0' ? 'table-danger' : s.transfer_room_id ? 'table-warning' : ''}>
                        <th scope="row" className="text-center">{index + 1}</th>
                        <td className="text-center">{s.st_idnumber}</td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <StudentAvatar photo={s.st_photo} name={s.st_name} />
                            <span>
                              {s.st_name}
                              {s.transfer_room_id && (
                                <small className="text-muted ms-2">(ย้ายไป ม.{s.transfer_class_id}/{s.transfer_room_no})</small>
                              )}
                            </span>
                          </div>
                        </td>
                        <td className="text-center">
                          {s.ic_class ? `${s.ic_class}/${s.isl_room_no}` : <span className="text-muted">-</span>}
                        </td>
                        <td className="text-center">
                          {s.dorm_name ? (
                            <span
                              className="text-primary"
                              style={{ cursor: 'pointer', textDecoration: 'underline' }}
                              title="ดูรายชื่อนักเรียนร่วมหอ"
                              onClick={() => openDormModal(s)}
                            >
                              {s.dorm_name}
                            </span>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td className={s.st_status === '0'
                          ? 'text-center text-danger'
                          : s.st_status === '1'
                          ? 'text-center text-primary'
                          : 'text-center text-success'
                        }>
                          {s.status_text}
                        </td>
                        <td className="text-center">
                          <Link
                            to={`/academic/student-info/${s.st_id}`}
                            className="btn btn-sm btn-outline-primary"
                            title="ดูข้อมูลนักเรียน (อ่านอย่างเดียว)"
                          >
                            <i className="ri-eye-line me-1"></i>ดูข้อมูล
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        )}

        <Modal show={!!dormModal} onHide={() => setDormModal(null)} centered scrollable size="lg">
          <Modal.Header closeButton>
            <Modal.Title as="h6">
              นักเรียนในหอ {dormModal?.dorm_name}
              {dormModal?.dorm_code && <span className="text-muted ms-1">({dormModal.dorm_code})</span>}
              {dormModal?.village_name && <span className="text-muted ms-2">หมู่บ้าน{dormModal.village_name}</span>}
              <Badge bg="secondary" className="ms-2">{dormResidents.length} คน</Badge>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-0">
            {!dormLoaded ? (
              <div className="text-center py-5 text-muted">กำลังโหลด...</div>
            ) : dormResidents.length === 0 ? (
              <div className="text-center py-5 text-muted">ไม่พบนักเรียนในหอนี้</div>
            ) : (
              <Table hover responsive className="mb-0 align-middle">
                <thead className="table-secondary">
                  <tr>
                    <th scope="col" className="text-center" style={{ width: 60 }}>ที่</th>
                    <th scope="col" className="text-center" style={{ width: 110 }}>เลขประจำตัว</th>
                    <th scope="col">ชื่อ-สกุล</th>
                    <th scope="col" className="text-center" style={{ width: 110 }}>ชั้นเรียน</th>
                  </tr>
                </thead>
                <tbody>
                  {dormResidents.map((r, index) => (
                    <tr key={r.st_id} className={r.st_id === dormModal?.st_id ? 'table-primary' : ''}>
                      <th scope="row" className="text-center">{index + 1}</th>
                      <td className="text-center">{r.st_idnumber}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <StudentAvatar photo={r.st_photo} name={r.st_name} />
                          <span>{r.st_name}</span>
                        </div>
                      </td>
                      <td className="text-center">{r.c_no && r.r_no ? `ม.${r.c_no}/${r.r_no}` : <span className="text-muted">-</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Modal.Body>
        </Modal>

        <Footer />
      </div>
    </React.Fragment>
  );
}
